import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import * as projectsApi from './projectsApi';
import type {
  CreateServerProjectBody,
  ServerProject,
  UpdateServerProjectBody,
} from './projectsApi';
import { useCurrentUser } from './useAuth';

/**
 * Server-projects cache keys. We index by user id so a sign-out +
 * sign-in-as-someone-else doesn't surface the previous kid's list.
 */
export function projectsQueryKey(userId: string | null): readonly unknown[] {
  return ['account', 'projects', userId] as const;
}

/**
 * List the signed-in kid's synced projects. When signed out the
 * query is disabled — TanStack Query returns `data: undefined` and
 * the consumer renders the local-only state.
 */
export function useServerProjects(): UseQueryResult<ServerProject[], Error> {
  const { data: user } = useCurrentUser();
  return useQuery({
    queryKey: projectsQueryKey(user?.id ?? null),
    queryFn: projectsApi.listServerProjects,
    enabled: !!user,
  });
}

export function useCreateServerProject(): UseMutationResult<
  ServerProject,
  Error,
  CreateServerProjectBody
> {
  const qc = useQueryClient();
  const { data: user } = useCurrentUser();
  return useMutation({
    mutationFn: (body: CreateServerProjectBody) => projectsApi.createServerProject(body),
    onSuccess: () => {
      // Refetch the list rather than splicing in — keeps the order
      // (newest-first by updated_at) honest without re-implementing
      // the sort client-side.
      void qc.invalidateQueries({ queryKey: projectsQueryKey(user?.id ?? null) });
    },
  });
}

export function useUpdateServerProject(): UseMutationResult<
  ServerProject,
  Error,
  { id: string; body: UpdateServerProjectBody }
> {
  const qc = useQueryClient();
  const { data: user } = useCurrentUser();
  return useMutation({
    mutationFn: ({ id, body }) => projectsApi.updateServerProject(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKey(user?.id ?? null) });
    },
  });
}

export function useDeleteServerProject(): UseMutationResult<void, Error, { id: string }> {
  const qc = useQueryClient();
  const { data: user } = useCurrentUser();
  return useMutation({
    mutationFn: ({ id }) => projectsApi.deleteServerProject(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKey(user?.id ?? null) });
    },
  });
}
